import { Close, SyncAltOutlined } from "@mui/icons-material";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import GroupIcon from "@mui/icons-material/Group";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  TextField,
  Tooltip,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useSnackbar } from "notistack";
import * as React from "react";
import { ReactElement, useContext, useEffect, useState } from "react";

import ShareLink from "../../../../components/dialogs/share-link";
import UserRole from "../../../../components/user-role/user-role";
import { AppContext } from "../../../../context/app-context";
import {
  Collection as GqlCollection,
  IamUser,
} from "../../../../generated/gql/graphql";
import useCollection from "../../../../hooks/mutation/collection/use-collection";
import useShareCollection from "../../../../hooks/mutation/collection/use-share-collection";
import useUsers from "../../../../hooks/query/user/use-users";
import Collection from "../../../../services/collection";
import { parseType } from "../../../../utils/graphql";

type Props = {
  imageId: string;
  collection: GqlCollection;
  classes?: string;
};

export default function ContextMenu(props: Props): ReactElement {
  const { classes, collection, imageId } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [users, setUsers] = useState<IamUser[]>([]);
  const [searchUsersParam, setSearchUsersParam] = useState<string | undefined>(
    ""
  );
  const { data: usersData } = useUsers({
    filter: {
      search: {
        icontains: searchUsersParam,
      },
    },
  });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [openSharedDialig, setOpenSharedDialog] = useState(false);
  const [shareLinkDialog, setShareLinkDialog] = useState(false);
  const [inputElement, setInputElement] = useState<string>(
    collection.name || ""
  );

  const { deleteCollection, updateCollection, syncCollectionAssets } =
    useCollection();
  const { shareCollection } = useShareCollection();

  const { getCollections, getSharedCollections, setShareCollection } =
    Collection();

  const {
    setFilterCollections,
    setCollections,
    setSharedCollections,
    filterCollections,
    collections,
    sorting,
    user,
    setCollectionId,
  } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!usersData) {
      return;
    }

    setUsers(parseType<IamUser[]>("users", usersData) ?? []);
  }, [usersData]);

  const handleClickContextMenu = (
    event: React.MouseEvent<HTMLElement>
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseContextMenu = (id: string): void => {
    setAnchorEl(null);
  };

  const handleOpenRenameDialog = (): void => {
    setOpenDialog(true);
  };

  const handleCloseRenameDialog = (): void => {
    setOpenDialog(false);
  };

  const handleOpenSharedDialog = (): void => {
    setOpenSharedDialog(true);
  };

  const handleOpenSharedLink = (): void => {
    setShareLinkDialog(true);
  };

  const handleCloseSharedDialog = (): void => {
    setOpenSharedDialog(false);
  };

  const handleRenameCollection = async (): Promise<void> => {
    setOpenDialog(false);
    setAnchorEl(null);

    await updateCollection(collection.id, {
      name: inputElement,
    });

    enqueueSnackbar(`Collection ${collection.name} was renamed.`, {
      variant: "success",
    });

    setFilterCollections((await getCollections(sorting)) ?? undefined);
    setSharedCollections((await getSharedCollections(sorting)) ?? undefined);
    setCollections((await getCollections(["name"])) ?? undefined);
  };

  const handleShareCollection = async (): Promise<void> => {
    if (selectedUsers.length === 0) {
      return;
    }

    setOpenSharedDialog(false);
    setAnchorEl(null);

    await shareCollection({
      collectionId: collection.id,
      userIds: selectedUsers,
    });

    await setShareCollection(collection.id, selectedUsers);

    enqueueSnackbar(`Collection ${collection.name} was shared.`, {
      variant: "success",
    });

    setSharedCollections((await getSharedCollections(sorting)) ?? undefined);
  };

  const handleDeleteCollection = (): void => {
    if (confirm("Are you sure?")) {
      (async () => {
        await deleteCollection(collection.id);

        enqueueSnackbar(`Collection ${collection.name} was deleted.`, {
          variant: "success",
        });

        setAnchorEl(null);
        setFilterCollections(() =>
          filterCollections?.filter((item) => item.id !== collection.id)
        );
        setCollections(() =>
          collections?.filter((item) => item.id !== collection.id)
        );
        setCollectionId(undefined);
      })();
    }
  };

  const handleChangeSharingUser = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (e.target.checked) {
      setSelectedUsers((prev) => [...prev, e.target.id]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id != e.target.id));
    }
  };

  async function handleSyncCollectionAssets(): Promise<void> {
    try {
      await syncCollectionAssets({
        collectionId: collection.id,
      });

      enqueueSnackbar(`Start syncing Collection ${collection.name}.`, {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to sync assets from ${collection.name}.`, {
        variant: "error",
      });
    }

    setAnchorEl(null);
  }

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchUsersParam(e.target.value);
  };

  return (
    <div className={classes}>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClickContextMenu}
      >
        <MoreVertIcon style={{ color: open ? "#DD2127" : "" }} />
      </IconButton>
      <Menu
        id="long-menu"
        className="shadow rounded-[15px]"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseContextMenu}
        PaperProps={{
          style: {
            width: "auto",
            padding: 0,
            borderRadius: "15px",
          },
        }}
      >
        <MenuItem
          onClick={handleOpenSharedDialog}
          sx={{ "&:hover": { color: "#999999" } }}
        >
          <Tooltip title="Share" placement="right">
            <GroupIcon />
          </Tooltip>
        </MenuItem>
        <MenuItem
          onClick={handleOpenSharedLink}
          sx={{ "&:hover": { color: "#999999" } }}
        >
          <Tooltip title="Copy share link" placement="right">
            <ShareOutlinedIcon />
          </Tooltip>
        </MenuItem>
        <MenuItem
          onClick={handleSyncCollectionAssets}
          sx={{
            "&:hover": { color: "#999999" },
          }}
          disabled={
            collection.externalServiceConnections
              ? collection.externalServiceConnections.length === 0
              : true
          }
        >
          <Tooltip title="Sync Assets" placement="right">
            <SyncAltOutlined />
          </Tooltip>
        </MenuItem>
        <MenuItem
          onClick={handleOpenRenameDialog}
          sx={{ "&:hover": { color: "#999999" } }}
        >
          <Tooltip title="Rename" placement="right">
            <ModeEditOutlineOutlinedIcon />
          </Tooltip>
        </MenuItem>
        <MenuItem disabled sx={{ "&:hover": { color: "#999999" } }}>
          <PhotoOutlinedIcon />
        </MenuItem>
        <UserRole>
          <MenuItem
            onClick={handleDeleteCollection}
            sx={{ "&:hover": { color: "#999999" } }}
          >
            <Tooltip title="Delete" placement="right">
              <DeleteForeverOutlinedIcon />
            </Tooltip>
          </MenuItem>
        </UserRole>
      </Menu>
      <Dialog open={openDialog} onClose={handleCloseRenameDialog}>
        <DialogTitle>Collection name</DialogTitle>
        <DialogContent>
          <TextField
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setInputElement(e.target.value)
            }
            autoFocus
            margin="dense"
            id="name"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={collection.name}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameDialog}>Cancel</Button>
          <Button
            disabled={!inputElement.replace(/\s/g, "").length}
            onClick={handleRenameCollection}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openSharedDialig} onClose={handleCloseSharedDialog}>
        <DialogTitle>Share collection with users:</DialogTitle>
        <DialogContent className="w-[400px] min-h-[400px]">
          <OutlinedInput
            className="w-full mb-[15px]"
            size="small"
            type="text"
            value={searchUsersParam ?? ""}
            placeholder="Search by name or email"
            onChange={handleChangeSearch}
            endAdornment={
              <InputAdornment position="end">
                {searchUsersParam && (
                  <Close
                    onClick={() => {
                      setSearchUsersParam(undefined);
                      setUsers(
                        usersData
                          ? parseType<IamUser[]>("users", usersData) ?? []
                          : []
                      );
                    }}
                    className="cursor-pointer"
                  />
                )}
              </InputAdornment>
            }
          />
          <FormGroup>
            {users.map((usr: IamUser) => {
              if (usr.id !== user?.id) {
                return (
                  usr.id && (
                    <FormControlLabel
                      disabled={collection.userId == usr.id}
                      sx={{
                        svg: { fontSize: "20px" },
                        span: { fontSize: "14px" },
                      }}
                      key={usr.id}
                      control={
                        <Checkbox
                          onChange={handleChangeSharingUser}
                          hidden={!usr.id}
                          checked={
                            collection.userId == usr.id ||
                            selectedUsers.includes(usr.id)
                          }
                          id={usr.id ?? undefined}
                        />
                      }
                      label={`${usr.firstname + " " + usr.lastname} ${
                        !usr.firstname && !usr.lastname ? usr.email : ""
                      } ${collection.userId == usr.id ? "(Owner)" : ""}`}
                    />
                  )
                );
              }
            })}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSharedDialog}>Cancel</Button>
          <Button disabled={!users.length} onClick={handleShareCollection}>
            Share
          </Button>
        </DialogActions>
      </Dialog>
      {shareLinkDialog && (
        <ShareLink
          isOpen={shareLinkDialog}
          setToggle={setShareLinkDialog}
          collectionId={collection.id.toString()}
          collectionName={collection.name ?? ""}
        />
      )}
    </div>
  );
}
